/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __ } from '@wordpress/i18n';

/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';

/**
 * WordPress components for the inspector controls.
 */
import { PanelBody, ToggleControl, TextControl } from '@wordpress/components';

/**
 * WordPress color picker component.
 */
import { ColorPicker } from '@wordpress/components';

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import './editor.scss';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 */
export default function Edit({ attributes, setAttributes }) {
	const {
		title,
		accentColor,
		showInterest
	} = attributes;

	return (
		<>
			<InspectorControls>
				<PanelBody title={__('Calculator Settings', 'blue-haven-loan-calculator')}>
					<TextControl
						label={__('Calculator Title', 'blue-haven-loan-calculator')}
						value={title}
						onChange={(value) => setAttributes({ title: value })}
						help={__('Enter the title for the loan calculator.', 'blue-haven-loan-calculator')}
					/>
					<div style={{ marginBottom: '16px' }}>
						<label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
							{__('Accent Color', 'blue-haven-loan-calculator')}
						</label>
						<ColorPicker
							color={accentColor}
							onChangeComplete={(color) => setAttributes({ accentColor: color.hex })}
							disableAlpha
						/>
						<p style={{ fontSize: '12px', color: '#666', margin: '8px 0 0' }}>
							{__('Choose the accent color for focus states, labels, and results.', 'blue-haven-loan-calculator')}
						</p>
					</div>
					<ToggleControl
						label={__('Show Interest Amount', 'blue-haven-loan-calculator')}
						checked={showInterest}
						onChange={(value) => setAttributes({ showInterest: value })}
						help={__('Display the total interest amount in addition to monthly payment.', 'blue-haven-loan-calculator')}
					/>
				</PanelBody>
			</InspectorControls>
			<div { ...useBlockProps() }>
				<div
					className="loan-calculator is-focused"
					style={{ '--accent-color': accentColor }}
				>
					<h3>{title}</h3>
					<form className="loan-calculator-form">
						<div className="loan-calculator-field is-focused">
							<label htmlFor="total-amount">Total Amount ($)</label>
							<input
								type="text"
								id="total-amount"
								defaultValue="100,000"
								inputMode="numeric"
								readOnly
							/>
						</div>
						<div className="loan-calculator-field is-focused">
							<label htmlFor="down-payment">Down Payment ($)</label>
							<input
								type="text"
								id="down-payment"
								defaultValue="20,000"
								inputMode="numeric"
								readOnly
							/>
						</div>
						<div className="loan-calculator-field is-focused">
							<label htmlFor="interest-rate">Interest Rate (%)</label>
							<input
								type="text"
								id="interest-rate"
								defaultValue="6.5"
								inputMode="decimal"
								readOnly
							/>
						</div>
						<div className="loan-calculator-field is-focused">
							<label htmlFor="amortization-period">Amortization Period (Months)</label>
							<input
								type="text"
								id="amortization-period"
								defaultValue="72"
								inputMode="numeric"
								readOnly
							/>
						</div>
						<div className="loan-calculator-results">
							<div className="monthly-payment">
								<strong>Monthly Payment: <span className="amount">$0.00</span></strong>
							</div>
							{showInterest && (
								<div className="total-interest">
									<strong>Total Interest: <span className="amount">$0.00</span></strong>
								</div>
							)}
						</div>
					</form>
					<p className="editor-note">
						<em>This is a preview. The calculator will be interactive on the frontend.</em>
					</p>
				</div>
			</div>
		</>
	);
}
